import { useParams, useNavigate } from 'react-router-dom';
import { Plus, List } from 'lucide-react';
import { type DragEndEvent } from '@dnd-kit/core';
import { usePipelineDeals } from '@/composables/crm/useDeals';
import { usePipelines } from '@/composables/crm/usePipeline';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { KanbanColumn } from '@/components/kanban/KanbanColumn';
import { KanbanCard } from '@/components/kanban/KanbanCard';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function DealPipelinePage() {
  const { pipelineId: paramPipelineId } = useParams<{ pipelineId: string }>();
  const navigate = useNavigate();
  const { pipelines, isLoading: pipelinesLoading } = usePipelines();

  const defaultPipeline = pipelines.find((p) => p.isDefault) || pipelines[0];
  const selectedPipelineId = paramPipelineId || defaultPipeline?.id || '';

  const { stages, isLoading, moveStage } = usePipelineDeals(selectedPipelineId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const dealId = active.id as string;
    const targetStageId = over.id as string;
    moveStage.mutate({ id: dealId, stageId: targetStageId });
  };

  if (pipelinesLoading) return <LoadingSpinner />;

  return (
    <PageContainer
      title="Deal Pipeline"
      description="Drag and drop deals between stages"
      actions={
        <div className="flex items-center gap-2">
          <Select value={selectedPipelineId} onValueChange={(v) => navigate(`/deals/pipeline/${v}`)}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Select pipeline" /></SelectTrigger>
            <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate('/deals')}><List className="mr-2 h-4 w-4" />List View</Button>
          <Button onClick={() => navigate('/deals/new')}><Plus className="mr-2 h-4 w-4" />New Deal</Button>
        </div>
      }
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <KanbanBoard onDragEnd={handleDragEnd}>
          {stages.map((stage) => (
            <KanbanColumn
              key={stage.id}
              id={stage.id}
              title={stage.name}
              count={stage.count}
              totalValue={stage.totalValue}
              color={stage.color}
              items={stage.deals.map((d) => d.id)}
            >
              {stage.deals.map((deal) => (
                <KanbanCard key={deal.id} deal={deal} />
              ))}
            </KanbanColumn>
          ))}
        </KanbanBoard>
      )}
    </PageContainer>
  );
}
